import { css } from '@emotion/react'
import { HFlow, InfoLabel, Modal, ModalBody, ModalHeader, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { HLabel } from 'components/HLabel'
import { IconLabel } from 'components/IconLabel'
import { LoadingIndicator } from 'components/loading'
import { isAfter, isBefore } from 'date-fns'
import { useHistoricoResultadosExamesQuery } from 'graphql/hooks.generated'
import {
  AcompanhamentoPreNatalStatusExameEnum,
  ExameEspecificoEnum,
  ResultadoExame,
  TipoAcompanhamentoEnum,
  TrimestreGestacional,
} from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { formatDate, toDate } from 'util/date/formatDate'
import { isObjectDeepFalsy } from 'util/object'
import { getResultadoExameEspecificoExtractValueFromQuery } from 'view/atendimentos/detail/soap/objetivo/resultados-exames/util-resultadosExames'
import { calculateTrimestreGestacional } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'

import { statusExamePreNatalRecord, trimestreGestacionalRecord } from '../model-acompanhamentoPreNatal'

interface DetalhamentoExameModalProps {
  prontuarioId: ID
  procedimentoId: ID
  nomeProcedimento: string
  codigoProcedimento: string
  registrosNoTrimestre?: number
  status?: AcompanhamentoPreNatalStatusExameEnum
  dataInicioCondicao: Date
  tipoAcompanhamento: TipoAcompanhamentoEnum
  onClose(): void
}

export function DetalhamentoExameModal(props: Readonly<DetalhamentoExameModalProps>) {
  const {
    prontuarioId,
    nomeProcedimento,
    codigoProcedimento,
    procedimentoId,
    registrosNoTrimestre,
    status,
    dataInicioCondicao,
    tipoAcompanhamento,
    onClose,
  } = props

  const isResultadoAnterior = status === AcompanhamentoPreNatalStatusExameEnum.RESULTADO_ANTERIOR
  const resultadoAnteriorLabel = statusExamePreNatalRecord[AcompanhamentoPreNatalStatusExameEnum.RESULTADO_ANTERIOR]
  const isAcompanhamentoPreNatal = tipoAcompanhamento === TipoAcompanhamentoEnum.PRE_NATAL

  const {
    data: { resultadosExame },
    loading,
  } = useHistoricoResultadosExamesQuery({
    variables: {
      input: {
        prontuarioId: prontuarioId,
        filtro: { procedimentoId: procedimentoId, dataInicial: !isResultadoAnterior ? dataInicioCondicao : null },
        pageParams: { unpaged: true },
      },
    },

    skip: !prontuarioId || !procedimentoId,
  })

  const totalRegistrosPreNatal = resultadosExame?.content?.filter(
    (resultadoExame: ResultadoExame) =>
      isAfter(toDate(resultadoExame.dataSolicitacao), dataInicioCondicao) ||
      isAfter(toDate(resultadoExame.dataRealizacao), dataInicioCondicao)
  ).length

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title={nomeProcedimento} subtitle={codigoProcedimento} />
      <ModalBody
        style={css`
          padding-top: 0;
        `}
      >
        {loading ? (
          <LoadingIndicator />
        ) : (
          <VFlow>
            {isAcompanhamentoPreNatal && (
              <HFlow hSpacing={1.5}>
                <HLabel title='Total de registros no trimestre:'>{registrosNoTrimestre}</HLabel>
                <HLabel title='Total de registros no pré-natal:'>{totalRegistrosPreNatal}</HLabel>
              </HFlow>
            )}
            {resultadosExame?.content?.map((resultadoExame: ResultadoExame) => (
              <Box key={resultadoExame.id}>
                <VFlow vSpacing={0.5}>
                  {isAcompanhamentoPreNatal && isBefore(toDate(resultadoExame.dataRealizacao), dataInicioCondicao) && (
                    <IconLabel
                      icon={resultadoAnteriorLabel.icon}
                      text={resultadoAnteriorLabel.label}
                      color={resultadoAnteriorLabel.color}
                      hSpacing={0.5}
                    />
                  )}
                  <HFlow hSpacing={1.5}>
                    <InfoLabel title='Data de realização'>
                      {getDataRealizacaoInfo(resultadoExame.dataRealizacao, dataInicioCondicao, tipoAcompanhamento)}
                    </InfoLabel>
                    <InfoLabel title='Data de resultado'>{formatDate(resultadoExame.dataResultado)}</InfoLabel>
                    <InfoLabel title='Data de solicitação'>{formatDate(resultadoExame.dataSolicitacao)}</InfoLabel>
                  </HFlow>
                  {!isObjectDeepFalsy(resultadoExame.especifico) && (
                    <InfoLabel title='Resultado'>
                      {resultadoExame.especifico.exameEspecificoEnum === ExameEspecificoEnum.PRENATAL
                        ? `Idade gestacional eco ${'semana'.pluralizeAndConcatValue(
                            resultadoExame.especifico.igSemanas ?? 0
                          )} e ${'dia'.pluralizeAndConcatValue(
                            resultadoExame.especifico.igDias ?? 0
                          )} - DPP eco ${formatDate(resultadoExame.especifico.dpp)}`
                        : getResultadoExameEspecificoExtractValueFromQuery(resultadoExame.exame)(resultadoExame)}
                    </InfoLabel>
                  )}
                  <InfoLabel title='Descrição'>{resultadoExame.resultado}</InfoLabel>
                  {resultadoExame.solicitacao && (
                    <HLabel title='Solicitado por:'>
                      {resultadoExame.solicitacao.atendimentoProfissional.lotacao.profissional.nome} -{' '}
                      {resultadoExame.solicitacao.atendimentoProfissional.lotacao.cbo.nome}
                    </HLabel>
                  )}
                  {resultadoExame.atendimentoProfissionalResultado && (
                    <HLabel title='Avaliado por:'>
                      {resultadoExame.atendimentoProfissionalResultado.lotacao.profissional.nome} -{' '}
                      {resultadoExame.atendimentoProfissionalResultado.lotacao.cbo.nome}
                    </HLabel>
                  )}
                </VFlow>
              </Box>
            ))}
          </VFlow>
        )}
      </ModalBody>
    </Modal>
  )
}

const getDataRealizacaoInfo = (
  dataRealizacao: LocalDate,
  dataInicioCondicao: Date,
  tipoAcompanhamento: TipoAcompanhamentoEnum
): string => {
  const dataFormatted = formatDate(dataRealizacao)

  const trimestreRealizacao =
    !isUndefinedOrNull(dataRealizacao) && tipoAcompanhamento === TipoAcompanhamentoEnum.PRE_NATAL
      ? calculateTrimestreGestacional(dataInicioCondicao, toDate(dataRealizacao))
      : null
  const hasTrimestre =
    !isUndefinedOrNull(trimestreRealizacao) && trimestreRealizacao !== TrimestreGestacional.FORA_PERIODO

  return `${dataFormatted}${hasTrimestre ? ` | ${trimestreGestacionalRecord[trimestreRealizacao]}` : ''}`
}
